<template>
  <div :class="shellClass" :style="shellStyle">

    <!-- TOOLBAR -->
    <div class="table-corporate-toolbar">

      <b-form
        v-if="showSearch"
        class="table-corporate-search"
        autocomplete="off"
        @submit.prevent
      >
        <b-form-input
          ref="searchInput"
          v-model.trim="tableFilter"
          :placeholder="effectiveLabels.searchPlaceholder"
          type="search"
          name="table_search"
          autocomplete="off"
          data-lpignore="true"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          size="sm"
          :disabled="toolbarDisabled"
        ></b-form-input>
      </b-form>

      <div class="table-corporate-actions">

        <!-- BOTÓN VOLVER (ahora a la derecha del filtro) -->
        <b-button
          v-if="showBackButton"
          variant="primary"
          size="sm"
          class="table-toolbar-back-btn mr-2"
          @click="$emit('back')"
          :disabled="toolbarDisabled"
        >
          <i class="fa-solid fa-folder-open mr-1"></i>
          {{ backButtonText }}
        </b-button>

        <b-button
          v-if="hasAdvancedFilters"
          variant="outline-secondary"
          size="sm"
          class="mr-2"
          @click="showAdvancedFilters = !showAdvancedFilters"
          :disabled="toolbarDisabled"
        >
          <i class="fa-solid fa-filter mr-1"></i>
          {{ effectiveLabels.filters }}
        </b-button>

        <b-dropdown
          v-if="allowColumnToggle"
          variant="outline-secondary"
          size="sm"
          right
          class="table-columns-dropdown"
          :disabled="toolbarDisabled"
        >
          <template #button-content>
            <i class="fa-solid fa-table-columns mr-1"></i>
            {{ effectiveLabels.columns }}
          </template>

          <b-dropdown-item-button @click="showAllColumns">
            {{ effectiveLabels.showAllColumns }}
          </b-dropdown-item-button>

          <b-dropdown-divider></b-dropdown-divider>

          <div class="table-columns-list px-3 py-2">
            <b-form-checkbox
              v-for="col in columnOptions"
              :key="col.value"
              :checked="!hiddenColumns.includes(col.value)"
              @change="(checked) => toggleColumn(col.value, checked)"
            >
              {{ col.text }}
            </b-form-checkbox>
          </div>
        </b-dropdown>

        <div class="table-corporate-slot-actions">
          <slot name="toolbar-actions"></slot>
        </div>

      </div>
    </div>


    <!-- FILTROS AVANZADOS -->
    <div
      v-if="hasAdvancedFilters && showAdvancedFilters"
      class="table-corporate-filters"
    >
      <div class="table-filter-grid">

        <template v-for="def in filterDefs">

          <b-form-select
            v-if="def.type === 'select'"
            :key="`${filterValueKey(def)}_select`"
            v-model="filterValues[filterValueKey(def)]"
            :options="def.options || []"
            size="sm"
            :disabled="toolbarDisabled"
          ></b-form-select>

          <b-form-input
            v-else-if="def.type === 'date'"
            :key="`${filterValueKey(def)}_date`"
            v-model="filterValues[filterValueKey(def)]"
            type="date"
            size="sm"
            :disabled="toolbarDisabled"
          ></b-form-input>

          <b-form-input
            v-else
            :key="`${filterValueKey(def)}_text`"
            v-model.trim="filterValues[filterValueKey(def)]"
            :placeholder="def.placeholder || ''"
            size="sm"
            :disabled="toolbarDisabled"
          ></b-form-input>

        </template>

        <b-form-select
          v-if="enableSorting"
          v-model="sortBy"
          :options="sortFieldOptions"
          size="sm"
          :disabled="toolbarDisabled"
        ></b-form-select>

        <b-form-select
          v-if="enableSorting"
          v-model="sortDirectionValue"
          :options="sortDirectionOptions"
          size="sm"
          :disabled="toolbarDisabled"
        ></b-form-select>

      </div>

      <div class="mt-2 d-flex justify-content-end">
        <b-button
          variant="outline-secondary"
          size="sm"
          @click="clearFilters"
          :disabled="toolbarDisabled"
        >
          {{ effectiveLabels.clearFilters }}
        </b-button>
      </div>
    </div>


    <!-- CUERPO DE LA TABLA -->
    <div
      class="table-corporate-body"
      :style="tableBodyStyle"
    >

      <!-- LOADING -->
      <div
        v-if="loading"
        class="table-corporate-loading-state"
      >
        <div class="table-corporate-empty-card">

          <div class="modern-spinner">
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
          </div>

          <p class="mt-2 mb-0">
            {{ effectiveLabels.loading }}
          </p>

        </div>
      </div>


      <!-- TABLA -->
      <b-table
        v-if="filteredItems.length > 0"
        :items="filteredItems"
        :fields="visibleFields"
        :per-page="showPagination ? perPage : filteredItems.length"
        :current-page="currentPage"
        :empty-text="currentEmptyText"
        :empty-filtered-text="effectiveLabels.noMatches"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="'last'"
        hover
        bordered
        responsive
        small
        class="tblEstandar tblCorporate"
        head-variant="custom-blue"
        @row-clicked="onRowClicked"
      >
        <template v-for="(_, slotName) in $slots" #[slotName]="slotData">
          <slot
            :name="slotName"
            v-bind="slotData"
          ></slot>
        </template>
      </b-table>


      <!-- MENSAJE CUANDO NO EXISTEN DATOS -->
      <div
        v-if="showInitialEmptyMessage && !loading"
        class="table-corporate-empty-state"
      >
        <div class="table-corporate-empty-card">

          <div class="table-corporate-empty-icon">
            <i class="fa-solid fa-table-list"></i>
          </div>

          <div class="table-corporate-empty-title">
            {{ effectiveLabels.initialEmptyTitle }}
          </div>

          <div class="table-corporate-empty-description">
            {{ effectiveLabels.initialEmptyMessage }}
          </div>

        </div>
      </div>

    </div>


    <!-- FOOTER - Solo mostrar si showPagination es true -->
    <div
      v-if="showPagination && filteredItems.length > 0"
      class="row col-12 mt-2 table-corporate-footer"
    >

      <div
        class="col-md-3 table-footer-block table-footer-left"
      ></div>

      <div
        class="col-md-6 d-flex justify-content-center align-items-center table-footer-block table-footer-center"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="filteredRows"
          :per-page="perPage"
          align="center"
          size="md"
          class="custom-pagination"
          first-number
          last-number
        ></b-pagination>
      </div>

      <div
        v-if="showRowsPerPage"
        class="col-md-3 d-flex justify-content-end align-items-center table-footer-block table-footer-right"
      >
        <label class="mr-2 mb-0">
          {{ effectiveLabels.rowsPerPage }}
        </label>

        <b-form-select
          v-model="perPage"
          :options="pageOptions"
          class="table-page-select"
          size="sm"
          style="width: 86px;"
        ></b-form-select>
      </div>

    </div>

  </div>
</template>


<script>
const DEFAULT_LABELS = {
  noRow: "No hay registros",
  noMatches: "No se encontraron resultados",

  initialEmptyTitle: "Aún no hay información para mostrar",

  initialEmptyMessage:
    "Ingresa los filtros o datos necesarios para comenzar a visualizar la información.",

  loading: "Cargando...",

  searchPlaceholder: "Buscar en la tabla...",
  rowsPerPage: "Filas por página:",
  totalRows: "Registros visibles",
  noSort: "Sin orden",
  sortAsc: "A-Z / Menor a mayor",
  sortDesc: "Z-A / Mayor a menor",
  columns: "Columnas",
  showAllColumns: "Mostrar todas",
  filters: "Filtros",
  clearFilters: "Limpiar filtros"
}


export default {
  name: 'CorporateDataTable',

  props: {
    items: {
      type: Array,
      default: () => []
    },

    loading: {
      type: Boolean,
      default: false
    },

    fields: {
      type: Array,
      default: () => []
    },

    filterDefs: {
      type: Array,
      default: () => []
    },

    searchKeys: {
      type: Array,
      default: null
    },

    labels: {
      type: Object,
      default: () => ({})
    },

    pageOptions: {
      type: Array,
      default: () => [5, 10, 20, 50, 100]
    },

    defaultPerPage: {
      type: Number,
      default: 10
    },

    allowColumnToggle: {
      type: Boolean,
      default: true
    },

    toolbarDisabled: {
      type: Boolean,
      default: false
    },

    enableSorting: {
      type: Boolean,
      default: true
    },

    showSearch: {
      type: Boolean,
      default: true
    },

    // NUEVA PROP: Controla si se muestra la paginación
    showPagination: {
      type: Boolean,
      default: true
    },

    // NUEVA PROP: Controla si se muestra el selector de filas por página
    showRowsPerPage: {
      type: Boolean,
      default: true
    },

    // NUEVA PROP: Muestra el botón Volver
    showBackButton: {
      type: Boolean,
      default: false
    },

    // NUEVA PROP: Texto del botón Volver
    backButtonText: {
      type: String,
      default: 'Volver'
    },

    narrow: {
      type: Boolean,
      default: true
    },

    maxWidth: {
      type: [String, Number],
      default: null
    },

    tableMinHeight: {
      type: [String, Number],
      default: 430
    },

    tableMaxHeight: {
      type: [String, Number],
      default: 430
    }
  },

  emits: ['row-clicked', 'back'],

  data() {
    return {
      perPage: this.defaultPerPage,
      currentPage: 1,
      tableFilter: '',
      showAdvancedFilters: false,
      hiddenColumns: [],
      filterValues: {},
      sortBy: null,
      sortDesc: false
    }
  },

  computed: {

    effectiveLabels() {
      return {
        ...DEFAULT_LABELS,
        ...this.labels
      }
    },


    shellClass() {
      return {
        'table-corporate-shell': true,
        'table-corporate-shell-narrow': this.narrow
      }
    },


    shellStyle() {
      if (!this.maxWidth) return {}

      const value =
        typeof this.maxWidth === 'number'
          ? `${this.maxWidth}px`
          : this.maxWidth

      return {
        maxWidth: value
      }
    },


    tableBodyStyle() {
      const min =
        typeof this.tableMinHeight === 'number'
          ? `${this.tableMinHeight}px`
          : this.tableMinHeight

      const max =
        typeof this.tableMaxHeight === 'number'
          ? `${this.tableMaxHeight}px`
          : this.tableMaxHeight

      return {
        minHeight: min,
        maxHeight: max
      }
    },


    hasAdvancedFilters() {
      return this.filterDefs.length > 0 || this.enableSorting
    },


    visibleFields() {
      return this.fields.filter(
        (field) => !this.hiddenColumns.includes(field.key)
      )
    },


    columnOptions() {
      return this.fields.map((field) => ({
        value: field.key,
        text: field.label
      }))
    },


    effectiveSearchKeys() {
      if (this.searchKeys?.length) {
        return this.searchKeys
      }

      return this.fields.map(
        (field) => field.key
      )
    },


    filteredItems() {
      const search =
        this.normalize(this.tableFilter)

      return (this.items || []).filter((item) => {

        const matchesAdvanced =
          this.filterDefs.every(
            (def) => this.matchesFilter(item, def)
          )

        if (!matchesAdvanced) {
          return false
        }

        if (!search) {
          return true
        }

        return this.effectiveSearchKeys.some(
          (key) =>
            this
              .normalize(
                this.getItemValue(item, key)
              )
              .includes(search)
        )
      })
    },


    filteredRows() {
      return this.filteredItems.length
    },


    /*
     * Determina si se debe mostrar la tarjeta
     * inicial de información.
     *
     * Solo depende de items originales.
     *
     * Si items está vacío:
     *     -> muestra la tarjeta.
     *
     * Si items tiene registros:
     *     -> la tarjeta desaparece.
     */
    showInitialEmptyMessage() {
      return !this.items || this.items.length === 0
    },


    /*
     * Evita mostrar detrás de nuestra tarjeta
     * el texto "No hay registros".
     *
     * Cuando no hay datos iniciales dejamos
     * vacío el mensaje interno de b-table.
     *
     * Cuando sí existen datos, conserva
     * el comportamiento original.
     */
    currentEmptyText() {
      if (this.showInitialEmptyMessage) {
        return ''
      }

      return this.effectiveLabels.noRow
    },


    sortFieldOptions() {
      const options = [
        {
          value: null,
          text: this.effectiveLabels.noSort
        }
      ]

      this.fields
        .filter(
          (field) => field.sortable !== false
        )
        .forEach((field) => {
          options.push({
            value: field.key,
            text: field.label
          })
        })

      return options
    },


    sortDirectionValue: {
      get() {
        return this.sortDesc
          ? 'desc'
          : 'asc'
      },

      set(value) {
        this.sortDesc = value === 'desc'
      }
    },


    sortDirectionOptions() {
      return [
        {
          value: 'asc',
          text: this.effectiveLabels.sortAsc
        },
        {
          value: 'desc',
          text: this.effectiveLabels.sortDesc
        }
      ]
    }
  },


  watch: {

    perPage() {
      this.currentPage = 1
    },


    tableFilter() {
      this.currentPage = 1
    },


    filterValues: {
      deep: true,

      handler() {
        this.currentPage = 1
      }
    },


    filterDefs: {
      deep: true,
      immediate: true,

      handler() {

        const nextValues = {}

        this.filterDefs.forEach((def) => {

          const valueKey =
            this.filterValueKey(def)

          nextValues[valueKey] =
            this.filterValues[valueKey] ?? ''
        })

        this.filterValues =
          nextValues
      }
    },


    toolbarDisabled(value) {
      if (value) {
        this.showAdvancedFilters = false
      }
    }
  },


  methods: {

    onRowClicked(
      item,
      index,
      event
    ) {
      this.$emit(
        'row-clicked',
        item,
        index,
        event
      )
    },


    showAllColumns() {
      this.hiddenColumns = []
    },


    toggleColumn(
      columnKey,
      checked
    ) {

      if (checked) {

        this.hiddenColumns =
          this.hiddenColumns.filter(
            (key) =>
              key !== columnKey
          )

        return
      }


      if (this.visibleFields.length === 1) {
        return
      }


      if (
        !this.hiddenColumns.includes(
          columnKey
        )
      ) {

        this.hiddenColumns.push(
          columnKey
        )
      }
    },


    clearFilters() {

      const reset = {}

      this.filterDefs.forEach((def) => {

        reset[
          this.filterValueKey(def)
        ] = ''
      })

      this.filterValues = reset
    },


    filterValueKey(def) {
      return def.id || def.key
    },


    getItemValue(
      item,
      key
    ) {

      if (!item || !key) {
        return ''
      }


      if (!key.includes('.')) {
        return item[key] ?? ''
      }


      return key
        .split('.')
        .reduce(
          (acc, part) =>
            acc?.[part] ?? '',
          item
        )
    },


    normalize(value) {
      return (value ?? '')
        .toString()
        .toLowerCase()
        .trim()
    },


    matchesFilter(
      item,
      def
    ) {

      const rawFilter =
        this.filterValues[
          this.filterValueKey(def)
        ]


      if (
        rawFilter === '' ||
        rawFilter === null ||
        rawFilter === undefined
      ) {
        return true
      }


      const itemKey =
        def.field || def.key


      const rawValue =
        this.getItemValue(
          item,
          itemKey
        )


      const mode =
        def.mode ||
        def.type ||
        'text'


      if (mode === 'dateFrom') {

        const value =
          (rawValue || '')
            .toString()
            .slice(0, 10)

        return value >= rawFilter
      }


      if (mode === 'dateTo') {

        const value =
          (rawValue || '')
            .toString()
            .slice(0, 10)

        return value <= rawFilter
      }


      if (
        mode === 'select' ||
        mode === 'equals'
      ) {
        return rawValue === rawFilter
      }


      return this
        .normalize(rawValue)
        .includes(
          this.normalize(rawFilter)
        )
    }
  }
}
</script>


<style scoped>

/*
 * CONTENEDOR DEL CUERPO DE LA TABLA
 *
 * position: relative permite que la tarjeta
 * se centre dentro de este espacio sin
 * modificar el tamaño del componente.
 */
.table-corporate-body {
  position: relative;
}


/*
 * CONTENEDOR DEL ESTADO VACÍO
 *
 * Ocupa exactamente el espacio disponible
 * del cuerpo de la tabla.
 */
.table-corporate-empty-state {
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;

  align-items: center;
  justify-content: center;

  /*
   * Evita que la tarjeta interfiera
   * con clicks de otros elementos.
   */
  pointer-events: none;

  /*
   * Se coloca por encima del contenido
   * vacío de b-table.
   */
  z-index: 10;

  padding: 20px;
}


/*
 * TARJETA MINIMALISTA
 *
 * Esta es la pequeña tarjeta que verá
 * el usuario cuando no existan datos.
 */
.table-corporate-empty-card {
  width: 100%;
  max-width: 480px;

  padding: 28px 32px;

  text-align: center;

  background: #ffffff;

  border: 1px solid #e4e8ed;

  border-radius: 10px;

  /*
   * Sombra muy suave para darle
   * profundidad sin verse pesada.
   */
  box-shadow:
    0 3px 12px rgba(0, 0, 0, 0.06);
}


/*
 * CÍRCULO DEL ÍCONO
 */
.table-corporate-empty-icon {
  width: 52px;
  height: 52px;

  margin: 0 auto 14px;

  display: flex;

  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: #f1f5f9;

  color: #54799f;

  font-size: 22px;
}


/*
 * TITULO
 */
.table-corporate-empty-title {
  margin-bottom: 8px;

  font-size: 18px;

  font-weight: 600;

  line-height: 1.35;

  color: #243447;
}


/*
 * DESCRIPCIÓN
 */
.table-corporate-empty-description {
  max-width: 390px;

  margin: 0 auto;

  font-size: 14px;

  font-weight: 400;

  line-height: 1.55;

  color: #6c757d;
}

.modern-spinner {
  position: relative;
  width: 60px;
  height: 60px;
  margin: 0 auto;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid transparent;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
}

.spinner-ring:nth-child(1) {
  border-top-color: #1f4f83;
  animation-delay: -0.45s;
}

.spinner-ring:nth-child(2) {
  border-right-color: #4a90d9;
  animation-delay: -0.3s;
}

.spinner-ring:nth-child(3) {
  border-bottom-color: #7fb3e6;
  animation-delay: -0.15s;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.table-corporate-loading-state {
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 20;
  padding: 20px;
}

.table-corporate-loading-state .table-corporate-empty-card {
  min-height: 180px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* =========================================================
   LAYOUT DE LA TOOLBAR - MANTENIENDO TAMAÑO ORIGINAL
   ========================================================= */

.table-corporate-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 6px 0;
}

/* El buscador mantiene su tamaño original (flex: 1 con max-width controlado) */
.table-corporate-search {
  flex: 1 1 auto;
  min-width: 150px;
  max-width: 280px;
}

/* Contenedor de acciones - se alinea a la derecha */
.table-corporate-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-left: auto;
}

.table-corporate-slot-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* =========================================================
   BOTÓN VOLVER
   ========================================================= */

.table-toolbar-back-btn {
  height: 31px;
  min-height: 31px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 7px;
  font-size: 13px;
  background-color: #1f4f83 !important;
  color: white !important;
  border: 1px solid #1f4f83 !important;
  flex-shrink: 0;
}

.table-toolbar-back-btn:hover:not(:disabled) {
  background-color: #1a446f !important;
  border-color: #1a446f !important;
}

.table-toolbar-back-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* =========================================================
   RESPONSIVE
   ========================================================= */

@media (max-width: 768px) {
  .table-corporate-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .table-corporate-search {
    flex: 1 1 100%;
    max-width: 100%;
    min-width: 100%;
  }

  .table-corporate-actions {
    margin-left: 0;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .table-toolbar-back-btn {
    width: auto;
    flex: 0 1 auto;
  }
}

@media (max-width: 576px) {
  .table-corporate-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .table-corporate-actions .btn,
  .table-corporate-actions .b-dropdown,
  .table-corporate-slot-actions {
    width: 100%;
  }

  .table-corporate-slot-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .table-toolbar-back-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>